import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import BidDetails from "./BidDetails";
import UpcomingBids from "./tables/UpcomingBids";
import useGetLiveCar from "../../../hooks/live-auction/useGetLiveCar";
import Pusher from "pusher-js";
import { getToken } from "../../../utils/storageUtils";
import VehicleDetails from "./tables/VehicleDetails";
import bidSound from "../../../assets/audios/bid-bell.mp3";
import useGetUpcomingBids from "../../../hooks/live-auction/useGetUpcomingBids";
import { BiRefresh } from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import { FaRegCalendarTimes } from "react-icons/fa";
import { FaArrowRightLong, FaArrowLeftLong } from "react-icons/fa6";
import newBidderSound from "../../../assets/audios/new-bidder.mp3";
import CountdownTimer from "./CountdownTimer";
import { getNextAuctionTimeEST } from "../../../utils/timeUtils";
import start321Sound from "../../../assets/audios/start321.mp3";

import useGetLocalCarDetail from "../../../hooks/useGetLocalCarsDetails";
import { useSearchParams } from "react-router-dom";

import PreviewVehicleAuction from "../../preview-live-auction/PreviewLiveAuctionCard";
import AuctionBidDetailsShimmer from "../../preview-live-auction/AuctionBidDetailsShimmer";
import LiveAuctionLoader from "./LiveAuctionLoader";

import Item from "../../live-aution-search/live-auction-search/Item";
import useGetAllLiveCars from "../../../hooks/live-auction/useGetAllLiveCars";

import moment from "moment-timezone";
import { useAuthContext } from "../../../hooks/useAuthContext";

const LiveAuctionDetail = () => {
  const { user } = useAuthContext();
  const userIdRef = useRef(user?.id);
  const userHasBidRef = useRef(false);
  const lastBidderIdRef = useRef("");
  const [searchParams] = useSearchParams();
  const previewId = searchParams.get("id");
  const navigate = useNavigate();
  const newBidSound = new Audio(bidSound);
  const newBidderAudio = useRef(new Audio(newBidderSound));
  const audioRef = useRef(new Audio(start321Sound));
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);

  // Get the next Wednesday in UTC format (or your local backend's expected timezone)

  const { liveCar, loading, error, fetchLiveCar } = useGetLiveCar();
  const {
    upcomingBids,
    loading: upcomingLoading,
    error: upcommingError,
    fetchUpcomingBids,
  } = useGetUpcomingBids();

  const {
    carDetailData: previewCarData,
    carDetailLoading: previewLoading,
    // carDetailError: previewError,
    fetchCarDetail,
  } = useGetLocalCarDetail(
    previewId ? `local-cars/get-car?id=${previewId}` : null
  );

  const { liveCars, fetchLiveCars } = useGetAllLiveCars();
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    fetchLiveCars();
  }, []);

  useEffect(() => {
    if (previewId) {
      fetchCarDetail();
    }
  }, [previewId, fetchCarDetail]);

  let filteredAuctions = [];

  if (liveCars && Object.keys(liveCars).length > 0) {
    // Sort the keys (dates) and pick the earliest future date
    const sortedDates = Object.keys(liveCars)
      .filter((date) => moment(date).isAfter(moment()))
      .sort((a, b) => moment(a).diff(moment(b)));

    if (sortedDates.length > 0) {
      const nearestDate = sortedDates[0];
      filteredAuctions = [[nearestDate, liveCars[nearestDate]]];
    }
  }

  const [members, setMembers] = useState([]);
  const [memberCount, setMemberCount] = useState(0);
  const [liveData, setLiveData] = useState({
    currentBid: 0,
    noOfBids: 0,
  });
  const [resetTimer, setResetTimer] = useState(false);
  const [bonusTime, setBonusTime] = useState(false);
  const [pusherStatus, setPusherStatus] = useState("");
  const [nextAuctionTime, setNextAuctionTime] = useState(null);
  const [isBidDisabled, setIsBidDisabled] = useState(false);
  const [userHasBid, setUserHasBid] = useState(false);
  const [userBidStatus, setUserBidStatus] = useState(null);
  const [initialLivePolling, setInitialLivePolling] = useState(false);

  // Track validity and initial loader timeout so notification-based join works correctly
  const isValidLiveCar =
    liveCar && liveCar.car && Object.keys(liveCar.car).length > 0;
  const isValidLiveCarRef = useRef(false);
  const initialPollingTimeoutRef = useRef(null);

  useEffect(() => {
    isValidLiveCarRef.current = isValidLiveCar;
    if (isValidLiveCar) {
      setInitialLivePolling(false);
      if (initialPollingTimeoutRef.current) {
        clearTimeout(initialPollingTimeoutRef.current);
        initialPollingTimeoutRef.current = null;
      }
    }
  }, [isValidLiveCar]);

  // Start the 30s \"join auction\" loader window:
  // - Only used when user is already on the timer screen and auction time arrives
  //   (either via countdown callback or backend notification).
  const startInitialJoinWindow = () => {
    // If we already have a valid live car, just show the auction – no loader
    if (isValidLiveCarRef.current) {
      return;
    }

    setInitialLivePolling(true);

    // Clear any existing timeout and start a fresh 30s window
    if (initialPollingTimeoutRef.current) {
      clearTimeout(initialPollingTimeoutRef.current);
    }
    initialPollingTimeoutRef.current = setTimeout(() => {
      setInitialLivePolling(false);
      initialPollingTimeoutRef.current = null;
      // Refresh next auction time so we show countdown to NEXT week, not the one that just passed.
      // This prevents onCountdownComplete from firing immediately and re-triggering the loader loop.
      setNextAuctionTime(getNextAuctionTimeEST());
    }, 30000);

    // Try to join the auction once immediately
    fetchLiveCar();
  };

  const handleRetry = () => {
    window.location.reload();
  };

  useEffect(() => {
    setPusherStatus(""); // Clear pusher status when liveCar changes
  }, [liveCar?.car?.id]);

  useEffect(() => {
    userIdRef.current = user?.id;
  }, [user?.id]);

  useEffect(() => {
    // Reset user bid state when the car changes
    setUserHasBid(false);
    setUserBidStatus(null);
    userHasBidRef.current = false;
    lastBidderIdRef.current = "";
  }, [liveCar?.car?.id]);

  // Initial fetch when user arrives / refreshes:
  // - If auction is live, they go straight to the auction UI.
  // - If not, backend returns an error and we show the timer screen.
  useEffect(() => {
    fetchLiveCar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // NOTE: We no longer auto-start the 30s loader on mount.
  // It should only appear when the user is already on the screen
  // and the backend notifies that the live auction has started.

  useEffect(() => {
    fetchUpcomingBids();
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  useEffect(() => {
    if (liveCar) {
      // Make liveCar data available globally for the CircularProgress component
      window.liveCar = liveCar;
    }
  }, [liveCar]);

  useEffect(() => {
    const pusher = new Pusher(process.env.REACT_APP_PUSHER_APP_ID, {
      cluster: "ap2",
      authEndpoint: `${process.env.REACT_APP_API_BASE_URL}pusher/auth/live-bidding`,
      auth: {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      },
    });

    // Subscribe to the presence channel
    const channel = pusher.subscribe(`presence-live-auction`);
    channel.bind("new-bid", (data) => {
      newBidSound.play();
      setLiveData({
        currentBid: data.message.bid_price,
        noOfBids: data.message.noOfBids,
        userID: data.message.userID,
      });
      lastBidderIdRef.current = data?.message?.userID;
      setBonusTime(false);
      setResetTimer(true);
      setIsBidDisabled(false);
      console.log("NEW BID...........................");

      // Update user bid banner status
      const bidderId = data?.message?.userID?.toString?.();
      const currentUserId = userIdRef.current?.toString?.();
      if (bidderId && currentUserId) {
        if (bidderId === currentUserId) {
          setUserHasBid(true);
          setUserBidStatus("winning");
          userHasBidRef.current = true;
        } else if (userHasBidRef.current) {
          setUserBidStatus("outbid");
          userHasBidRef.current = true;
        }
      }
    });

    channel.bind("bonus-time", (data) => {
      setBonusTime(true);
      setResetTimer(true);
      setIsBidDisabled(false);
      console.log("BONUS TIME...........................");
    });

    // Backend notifies when the live auction actually starts
    channel.bind("live-auction-started", (data) => {
      console.log("LIVE AUCTION STARTED notification received:", data);
      // If user is already on the screen and we still don't have a live car,
      // start (or extend) the 30s join window and retry fetching.
      startInitialJoinWindow();
    });
    // Add car-status handler
    channel.bind("car-status", (data) => {
      setPusherStatus(data.message.message);
      setIsBidDisabled(true);
      const lastWinnerId = (lastBidderIdRef.current ?? "").toString();
      const currentUserId = userIdRef.current?.toString?.();
      if (userHasBidRef.current && currentUserId) {
        if (lastWinnerId && lastWinnerId === currentUserId) {
          setUserBidStatus("won");
        } else {
          setUserBidStatus("lost");
        }
      }
      console.log("car-status...........................");
    });

    channel.bind("end-auction", (data) => {
      console.log("end-auction 000...........................");
      setPusherStatus(""); // Clear previous car's status
      fetchLiveCar().then(() => {
        setResetTimer(true);
        setBonusTime(false);
        setIsBidDisabled(false);
      });
      setLiveData({
        currentBid: "",
        noOfBids: "",
        userID: "",
      });
      lastBidderIdRef.current = "";
      fetchUpcomingBids();
    });

    // channel.bind("auctionCompleted", (data) => {
    // });

    if (!error) {
      // Track initial subscription success
      channel.bind("pusher:subscription_succeeded", (members) => {
        const allMembers = Object.values(members.members);
        setMembers(allMembers);
        setMemberCount(members.count);
      });
    }

    if (!error) {
      channel.bind("pusher:member_added", (member) => {
        setMembers((prev) => [...prev, member.info]);
        setMemberCount((prev) => prev + 1);
        // Play new bidder sound
        if(isValidLiveCar){
          const playNewBidderSound = async () => {
          try {
            newBidderAudio.current.currentTime = 0; // Reset audio position
            await newBidderAudio.current.play();
          } catch (error) {}
        };
        playNewBidderSound();   
      }
      });
    }

    if (!error) {
      channel.bind("pusher:member_removed", (member) => {
        setMembers((prev) => prev.filter((m) => m.id !== member.id));
        setMemberCount((prev) => prev - 1);
      });
    }

    // Cleanup on unmount
    return () => {
      channel.unsubscribe();
      pusher.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setNextAuctionTime(getNextAuctionTimeEST());
  }, []);

  const handleFourSecondsLeft = () => {
    // When the countdown reaches 4 seconds, play the 3-2-1 audio.
    // Do NOT show loader here - wait for countdown to reach 0 (handleCountdownComplete).
    if (!isAudioPlaying && audioRef.current) {
      setIsAudioPlaying(true);
      const audio = audioRef.current;
      audio.currentTime = 0; // Reset to start for replay
      audio.play().catch(() => {
        // Browser may block autoplay; loader will still show when countdown hits 0
      });
    }
  };

  const handleCountdownComplete = () => {
    // Countdown has reached 0 - NOW show loader and try to connect to auction server.
    startInitialJoinWindow();
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.preload = "auto"; // Preload so 3-2-1 is ready when countdown hits 4 sec
    }
    return () => {
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    };
  }, []);

  if (initialLivePolling) {
    return (
      <LiveAuctionLoader
        message="Auction is starting..."
        subtext="We're connecting you to the live auction. This may take a few seconds."
      />
    );
  }

  if (loading && isValidLiveCar) {
    return <AuctionBidDetailsShimmer  loading= {true}/>;
  }

  // if (previewId && previewLoading) {
  //   return <div>Loading...</div>;
  // }

  console.log("error:", error);
  console.log("previewId:", previewId);
  console.log("previewCarData:", previewCarData);
  console.log("previewCarData?.data?.car:", previewCarData?.data?.car);

  return (
    <>
      <div className="hidden lg:block live-auction-list">
        <div className="w-[15.5] flex flex-col pt-[20vh]">
          <div className="text-[2.6vw] font-semibold text-white">
            Live Auction
          </div>
          <div className="text-white flex gap-3 justify-center text-[1vw] font-urbanist">
            <Link to="/">
              <button className="hover:text-white hover:scale-110 duration-150">
                Home
              </button>
            </Link>
            /
            <button className="hover:text-white hover:scale-110 duration-150">
              Live Auction
            </button>
          </div>
        </div>
      </div>

      {error && previewId && previewCarData?.data?.car ? (
        <PreviewVehicleAuction car={previewCarData.data.car} loading={previewLoading} />
      ) : error ? (
        <div>
          <div className="flex justify-center items-center h-full lg:h-[80vh] mt-[80px] lg:mt-0">
            <div className="flex flex-col w-full lg:w-[80vw] items-center justify-center p-8 bg-gray-50 rounded-lg text-center space-y-6 shadow-lg">
              {/* Error Icon and Title */}
              <div className="flex flex-col items-center gap-3 text-gray-600">
                <FaRegCalendarTimes size={48} className="text-red-500" />
                <h1 className="text-3xl font-bold">No Live Auction Active</h1>
              </div>

              {/* Countdown Timer */}
              <p className="text-lg text-gray-700 font-medium">
                The next auction starts in:
              </p>
              {nextAuctionTime ? (
                <CountdownTimer
                  targetDate={nextAuctionTime}
                  onFourSecondsLeft={handleFourSecondsLeft}
                  onCountdownComplete={handleCountdownComplete}
                />
              ) : (
                <p className="text-gray-500">Calculating time...</p>
              )}

              {/* Informational Message */}
              <p className="text-md text-gray-600 mt-2">
                {(() => {
                  // Map numeric weekday (0=Sunday .. 6=Saturday) to day name
                  const weekdayNum = Number(process.env.REACT_APP_AUCTION_DAY);
                  const weekdayNames = [
                    "Sunday",
                    "Monday",
                    "Tuesday",
                    "Wednesday",
                    "Thursday",
                    "Friday",
                    "Saturday",
                  ];
                  const weekdayStr =
                    weekdayNum >= 0 && weekdayNum <= 6
                      ? weekdayNames[weekdayNum]
                      : `Day ${process.env.REACT_APP_AUCTION_DAY}`;
                  return (
                    <>
                      The next Auction will be held on {weekdayStr} at {process.env.REACT_APP_AUCTION_HOUR}:{process.env.REACT_APP_AUCTION_MINUTE} AM EST.
                    </>
                  );
                })()}
              </p>

              {/* Actions */}
              <div className="flex lg:flex-row flex-col gap-2 lg:gap-4 mt-4">
                {/* Back Button */}
                <button
                  onClick={() => navigate(-1)}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg shadow-md hover:bg-gray-300 transition-all"
                >
                  <FaArrowLeftLong size={20} />
                  Go Back
                </button>
                {/* Retry Button */}
                <button
                  onClick={handleRetry}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg shadow-md hover:bg-red-700 transition-all"
                >
                  <BiRefresh size={20} />
                  Check Again
                </button>
                {/* Upcoming Button */}
                <button
                  onClick={() => navigate("/live-auction-search")}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg shadow-md hover:bg-blue-600 transition-all"
                >
                  Upcoming Auctions
                  <FaArrowRightLong size={20} />
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Upcoming Auctions Section */}
      {error && (
        <div className="max-w-[85vw] mx-auto px-4 py-8 mt-6">
          <h1 className="text-36 font-bold my-2">Upcoming Auctions</h1>

          {loading ? (
            <div className="text-center py-8 md:py-[2.625vw]">
              <p className="text-gray-500">Checking For Upcoming Auctions</p>
            </div>
          ) : filteredAuctions.length > 0 ? (
            filteredAuctions.map(([date, cars]) => (
              <div key={date} className="my-4">
                <h2 className="text-lg font-bold text-left">
                  {moment(date).format("dddd, MMMM Do YYYY, h:mm A")}
                </h2>
                {cars.map((car) => (
                  <div className="my-2" key={car.id}>
                    <Item car={car} isToday={false} showJoinButton={true} />
                  </div>
                ))}
              </div>
            ))
          ) : (
            <div className="text-center lg:text-[1vw] py-8 md:py-[2.625vw]">
              <p className="text-gray-500">No upcoming auctions available.</p>
            </div>
          )}
        </div>
      )}

      <div className=" bg-gray-100 w-[100vw] py-[40px] md:py-[1.25vw] mt-[80px] lg:mt-0">
        <div className="w-[90%] lg:max-w-[85vw] flex flex-col gap-5 lg:grid lg:grid-cols-12 mx-auto">
          {!error && liveCar && !loading && (
            <>
              <div className="col-span-7">
                {user && userHasBid && userBidStatus && (
                  <div
                    className={`mt-2 mb-2 p-3 text-center font-semibold rounded-md text-white ${
                      userBidStatus === "winning"
                        ? "bg-green-600"
                        : userBidStatus === "outbid"
                        ? "bg-orange-500"
                        : userBidStatus === "won"
                        ? "bg-green-700"
                        : "bg-red-600"
                    }`}
                  >
                    {userBidStatus === "winning" && "You are currently Winning"}
                    {userBidStatus === "outbid" && "You have been Outbid"}
                    {userBidStatus === "won" && `🎉 Congratulations! You won this car for $${liveData?.currentBid}! An agent will contact you shortly to proceed with payment.`}
                    {userBidStatus === "lost" && "❌ Sorry, you were outbid. Better luck next time!"}
                  </div>
                )}
                <BidDetails
                  liveCar={liveCar}
                  liveData={liveData}
                  members={members}
                  memberCount={memberCount}
                  resetTimer={resetTimer}
                  setResetTimer={setResetTimer}
                  bonusTime={bonusTime}
                  setBonusTime={setBonusTime}
                  pusherStatus={pusherStatus}
                  isBidDisabled={isBidDisabled}
                />
              </div>

              {!upcomingLoading && !upcommingError && upcomingBids && (
                <div className="flex flex-col   col-span-5 w-full">
                  <VehicleDetails vehicle={liveCar?.car} />
                  <UpcomingBids upcomingBids={upcomingBids} />
                </div>
              )}
            </>
          )}
          {!error && !liveCar && !loading && (
            <div className="text-center py-20">No live car data available.</div>
          )}
        </div>
      </div>
    </>
  );
};

export default LiveAuctionDetail;
